import type { UserRole } from "@/services/firebaseService";

/**
 * KB-3064 — what the sign-in allowlist keys on, now that it may not key on the
 * existence of `users/{uid}`.
 *
 * ## Why existence stopped being usable
 *
 * `users/{uid}` is an ESTATE-WIDE identity document, not a pro membership
 * record. Two server-side writers create it for any authenticated account, in
 * the Firebase project pro shares with kidsbooked.com and the Flutter app
 * (`kb-prod-fb-proj`):
 *
 *   - `beforeusercreated` — a deployed `beforeUserCreated` BLOCKING trigger
 *     (kb-ff `firebase/functions/index.js:1254`), which writes the doc during
 *     account creation, before any client can observe the account existing; and
 *   - `ensureUserIdentityBaseline` — a deployed callable (kb-ff
 *     `firebase/functions/shared/user_identity_baseline.js:369`) that pro's own
 *     `AuthContext` fires for every non-anonymous signed-in user, ungated.
 *
 * So "has a users doc" is true for every consumer who ever signed up on
 * kidsbooked.com, and for any stranger the moment they authenticate. It never
 * meant "approved for pro".
 *
 * ## Why `role`
 *
 * Both writers stamp `role: 'none'` on an identity nobody has authorized
 * (`index.js:1334`, `user_identity_baseline.js:285`), and `role` is
 * **server-authoritative**: the Firestore rules deny a caller authoring their
 * own on both sides of the loop — `userCreateHasNoSelfGrant` blocks any
 * non-`none` role at create, and the owner-update arm lists `role` among the
 * fields an owner may never touch. That is pinned, executably and two-sided, in
 * `tests/rules/loginAllowlistGate.kb3064.firestore.test.ts`.
 *
 * It is also readable at the moment the gate runs, which the alternatives are
 * not — and that is the constraint that actually decided this:
 *
 *   - **An approved-request record cannot be used.** `hasApprovedAccountRequest`
 *     LISTs `accountRequests` filtered on `email` + `status`, while the read rule
 *     keys on `requesterUid`; a list is evaluated against the query, so it is
 *     denied for every non-admin — including an approved applicant reading their
 *     own approval. Nor could a `requesterUid` filter rescue it: the requests are
 *     written UNAUTHENTICATED and carry no such field.
 *   - **A custom claim cannot be used.** `AuthContext` re-syncs claims only when
 *     the role claim is absent, and kb-ff's own rules record claim-less permanent
 *     staff as "the normal case, not an edge one". Gating on a claim would lock
 *     out ordinary staff.
 *
 * ## Allowlist, not a `!== 'none'` denylist
 *
 * `UserRole` does not include `'none'`, so the type does not describe the data
 * and cannot be leaned on here. Enumerating the roles that DO grant access means
 * an unrecognised value — a new server-side state, a typo, a future migration
 * stamping something else on unapproved identities — fails CLOSED. A
 * `role !== 'none'` test would admit every one of those.
 */

/**
 * May a holder of this role sign in to pro at all?
 *
 * Typed `Record<UserRole, boolean>` on purpose: it is **exhaustive**, so adding a
 * member to `UserRole` stops this file compiling until somebody decides whether
 * the new role may sign in. A bare string array would let a new role be denied
 * login silently — the same shape of silent-denial defect this ticket is about,
 * pointed the other way.
 *
 * Every value here is `true` today, and that is not an accident worth
 * simplifying away: the four `UserRole` members are the complete set of role
 * literals in `src/` (measured — the only other value that appears anywhere is
 * `'none'`, which is not a `UserRole`), so this table is a superset of every
 * role set any pro surface gates on — `ProtectedRoute`'s `requiredRoles`,
 * `TokenRoute`'s `isPrivileged`, `Index`'s redirect ladder. That containment is
 * what makes this gate incapable of locking out someone a downstream surface
 * would have admitted. Setting one to `false` breaks that property; if you do
 * it, check those three call sites.
 */
const SIGN_IN_ALLOWED: Record<UserRole, boolean> = {
  admin_user: true,
  camp_manager: true,
  camp_contributor: true,
  camp_user: true,
};

export const PRO_ACCESS_ROLES: readonly UserRole[] = (
  Object.keys(SIGN_IN_ALLOWED) as UserRole[]
).filter((role) => SIGN_IN_ALLOWED[role]);

/**
 * True only for a profile carrying a role a human granted. Deliberately total:
 * `null`, `undefined`, a non-object, a missing `role`, `'none'`, and any
 * unrecognised or non-string value all return false.
 *
 * The parameter is `unknown` on purpose. This reads a **Firestore document**,
 * whose shape no type in this repo actually guarantees — `UserProfile` declares
 * `role: UserRole`, a union that does not even include `'none'`, which every
 * baseline-written doc carries. Typing the parameter to the interface would let
 * the compiler assert a guarantee the data does not honour, on the one function
 * whose whole job is to not believe the document.
 *
 * This answers "may this account sign in to pro at all". It is NOT an
 * authorization check for any particular surface — routes gate on their own
 * `requiredRoles`, and Firestore rules gate the data. Do not reuse it as one.
 */
export function hasProAccessRole(profile: unknown): boolean {
  if (!profile || typeof profile !== "object") return false;
  const role = (profile as { role?: unknown }).role;
  if (typeof role !== "string") return false;
  // `=== true` is load-bearing, not defensive noise: a bare truthiness test on
  // `SIGN_IN_ALLOWED[role]` would admit inherited `Object.prototype` members —
  // `role: "constructor"` resolves to a function, which is truthy. Pinned.
  return SIGN_IN_ALLOWED[role as UserRole] === true;
}
